import {isNull, isUndefined} from 'lodash';

import {
  collectRecordKeys,
  collectRecordValues,
} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {RBusinessHour} from '../../../../models/api/businessHoursModel';
import {CalendarWeekDaysShortEnum} from '../../../../models/api/calendarCommonTypes';
import {TeamPreferencesBusinessHours} from '../../../../models/api/teamPreferencesModel';
import {computeHourFromBusinessHoursTime, defaultBusinessHours} from './common';

// Business Hour limits used for choosing how best to display the heatmap
export interface BusinessHourLimits {
  opensHour: number;
  closesHour: number;
  days: number[];
  timezone?: string;
}

const weekDays: Record<CalendarWeekDaysShortEnum, number> = {
  [CalendarWeekDaysShortEnum.MONDAY]: 1,
  [CalendarWeekDaysShortEnum.TUESDAY]: 2,
  [CalendarWeekDaysShortEnum.WEDNESDAY]: 3,
  [CalendarWeekDaysShortEnum.THURSDAY]: 4,
  [CalendarWeekDaysShortEnum.FRIDAY]: 5,
  [CalendarWeekDaysShortEnum.SATURDAY]: 6,
  [CalendarWeekDaysShortEnum.SUNDAY]: 7,
};

export const computeLimitsFromDefaultBusinessHours = () => ({
  opensHour: computeHourFromBusinessHoursTime(defaultBusinessHours.opens),
  closesHour: computeHourFromBusinessHoursTime(defaultBusinessHours.closes),
  days: collectRecordValues(weekDays),
});

export const computeLimitsFromBusinessHours = (
  businessHours: TeamPreferencesBusinessHours,
): BusinessHourLimits => {
  // Use the defaults if business hours not enabled or set
  if (!businessHours.enabled) {
    return computeLimitsFromDefaultBusinessHours();
  }

  const daysArray = collectRecordKeys(CalendarWeekDaysShortEnum);

  const daysBusinessHours = daysArray
    .map((day) => businessHours[CalendarWeekDaysShortEnum[day]])
    .filter(RBusinessHour.is);

  const businessHourLimitsOpen = daysBusinessHours.reduce((currentOpenLimit, dayHours) => {
    const dayOpenTime = computeHourFromBusinessHoursTime(dayHours.opens);

    // Check if this open time is earlier than the earliest current opening time
    if (!currentOpenLimit || dayOpenTime < currentOpenLimit) {
      return dayOpenTime;
    }

    return currentOpenLimit;
  }, undefined as number | undefined);

  const businessHourLimitsClose = daysBusinessHours.reduce((currentCloseLimit, dayHours) => {
    const dayCloseTime = computeHourFromBusinessHoursTime(dayHours.closes);

    // Check if this close time is later than the latest current closing time
    if (!currentCloseLimit || dayCloseTime > currentCloseLimit) {
      return dayCloseTime;
    }

    return currentCloseLimit;
  }, undefined as number | undefined);

  const businessHourLimitsDays = daysArray.reduce((currentDays, day) => {
    const dayHours = businessHours[CalendarWeekDaysShortEnum[day]];
    if (!dayHours) {
      return currentDays;
    }
    const dayNumber = weekDays[CalendarWeekDaysShortEnum[day]];

    return [...currentDays, dayNumber];
  }, [] as number[]);

  // Use the defaults if any value is undefined after iterating through all the times
  // in the currently set business hours
  const opensHour = isUndefined(businessHourLimitsOpen)
    ? computeHourFromBusinessHoursTime(defaultBusinessHours.opens)
    : businessHourLimitsOpen;
  const closesHour = isUndefined(businessHourLimitsClose)
    ? computeHourFromBusinessHoursTime(defaultBusinessHours.closes)
    : businessHourLimitsClose;
  const days = businessHourLimitsDays.length > 0 ? businessHourLimitsDays : collectRecordValues(weekDays);
  const timezone = !isNull(businessHours.tz) ? businessHours.tz : undefined;

  return {opensHour, closesHour, days, timezone};
};
